/**
 * AddReminderModal — collect reminder details (title, due date, priority,
 * channels) and submit. Mirrors the DocumentUploadModal pattern.
 *
 * The parent supplies `entityType` / `entityId` at submit time, so this modal
 * only collects the user-entered fields.
 */

import { useEffect, useState } from 'react'
import { X, Bell, AlertCircle } from 'lucide-react'
import {
  REMINDER_CHANNEL_OPTIONS,
  REMINDER_CHANNEL_LABEL,
  REMINDER_PRIORITY_LABEL,
  REMINDER_RECURRENCE_OPTIONS,
  REMINDER_RECURRENCE_LABEL,
} from '@/types/reminder.types'
import type {
  ReminderPriority,
  ReminderChannel,
  ReminderRecurrence,
} from '@/types/reminder.types'
import { useUserStore } from '@/store/userStore'
import { useAuth } from '@/contexts/AuthContext'

/** The fields the modal collects — the parent adds entityType/entityId. */
export interface AddReminderFormValues {
  title: string
  dueDate: string // ISO datetime
  description?: string
  priority: ReminderPriority
  notifyChannels: ReminderChannel[]
  assignedToUserId?: string
  assignedToName?: string
  recurrence: ReminderRecurrence
  remindBeforeMinutes?: number
}

interface AddReminderModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (values: AddReminderFormValues) => Promise<void>
}

const PRIORITY_OPTIONS: ReminderPriority[] = ['low', 'medium', 'high', 'urgent']

/** Default due date = tomorrow 09:00, formatted for a datetime-local input. */
function defaultDueLocal(): string {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  d.setHours(9, 0, 0, 0)
  // datetime-local wants `YYYY-MM-DDTHH:mm` in local time.
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export default function AddReminderModal({ isOpen, onClose, onSubmit }: AddReminderModalProps) {
  const { user: currentUser } = useAuth()
  const [title, setTitle] = useState('')
  const [dueLocal, setDueLocal] = useState(defaultDueLocal())
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState<ReminderPriority>('medium')
  const [channels, setChannels] = useState<ReminderChannel[]>(['in_app'])
  const [assignedUserId, setAssignedUserId] = useState('')
  const [recurrence, setRecurrence] = useState<ReminderRecurrence>('none')
  const [remindBeforeMinutes, setRemindBeforeMinutes] = useState(1440)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')

  const users = useUserStore((s) => s.users)
  const loadUsers = useUserStore((s) => s.loadUsers)

  useEffect(() => {
    if (isOpen && users.length === 0 && currentUser?.tenantId) {
      loadUsers(currentUser.tenantId)
    }
  }, [isOpen, users.length, currentUser?.tenantId, loadUsers])

  if (!isOpen) return null

  const reset = () => {
    setTitle('')
    setDueLocal(defaultDueLocal())
    setDescription('')
    setPriority('medium')
    setChannels(['in_app'])
    setAssignedUserId('')
    setRecurrence('none')
    setRemindBeforeMinutes(1440)
    setError('')
  }

  const toggleChannel = (c: ReminderChannel) =>
    setChannels((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!title.trim()) {
      setError('Give the reminder a title')
      return
    }
    if (!dueLocal) {
      setError('Pick a due date')
      return
    }
    setIsSubmitting(true)
    try {
      const assignedUser = users.find((u) => u.userId === assignedUserId)
      await onSubmit({
        title: title.trim(),
        dueDate: new Date(dueLocal).toISOString(),
        description: description.trim() || undefined,
        priority,
        notifyChannels: channels.length > 0 ? channels : ['in_app'],
        assignedToUserId: assignedUserId || undefined,
        assignedToName: assignedUser ? assignedUser.fullName || assignedUser.email : undefined,
        recurrence,
        remindBeforeMinutes,
      })
      reset()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add reminder')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-surface-dark rounded-xl shadow-2xl border border-surface-overlay w-full max-w-md mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-surface-overlay flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
              <Bell className="w-5 h-5 text-primary-600" />
            </div>
            <h2 className="text-lg font-semibold text-text-primary">Add Reminder</h2>
          </div>
          <button type="button" onClick={onClose} className="btn btn-ghost btn-sm btn-square" disabled={isSubmitting}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4 overflow-y-auto flex-1">
          {error && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1.5">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Collect Form 16"
              className="input input-bordered w-full"
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1.5">
              Due date <span className="text-red-500">*</span>
            </label>
            <input
              type="datetime-local"
              value={dueLocal}
              onChange={(e) => setDueLocal(e.target.value)}
              className="input input-bordered w-full"
              disabled={isSubmitting}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1.5">Priority</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as ReminderPriority)}
                className="select select-bordered w-full"
                disabled={isSubmitting}
              >
                {PRIORITY_OPTIONS.map((p) => (
                  <option key={p} value={p}>
                    {REMINDER_PRIORITY_LABEL[p]}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1.5">Recurrence</label>
              <select
                value={recurrence}
                onChange={(e) => setRecurrence(e.target.value as ReminderRecurrence)}
                className="select select-bordered w-full"
                disabled={isSubmitting}
                aria-label="Recurrence"
              >
                {REMINDER_RECURRENCE_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {REMINDER_RECURRENCE_LABEL[r]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1.5">Assign to</label>
            <select
              value={assignedUserId}
              onChange={(e) => setAssignedUserId(e.target.value)}
              className="select select-bordered w-full"
              disabled={isSubmitting}
              aria-label="Assign to"
            >
              <option value="">Unassigned</option>
              {users.map((u) => (
                <option key={u.userId} value={u.userId}>
                  {u.fullName || u.email}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1.5">Alert before due date</label>
            <select
              value={remindBeforeMinutes}
              onChange={(e) => setRemindBeforeMinutes(Number(e.target.value))}
              className="select select-bordered w-full"
              disabled={isSubmitting}
              aria-label="Alert before due date"
            >
              <option value={0}>At time of due date</option>
              <option value={15}>15 minutes before</option>
              <option value={60}>1 hour before</option>
              <option value={1440}>1 day before</option>
              <option value={2880}>2 days before</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1.5">Notify via</label>
            <div className="flex flex-wrap gap-2">
              {REMINDER_CHANNEL_OPTIONS.map((c) => {
                const active = channels.includes(c)
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => toggleChannel(c)}
                    disabled={isSubmitting}
                    className={`text-sm px-3 py-1.5 rounded-lg border transition-colors ${
                      active
                        ? 'bg-primary-100 dark:bg-primary-900/30 border-primary-500 text-primary-700 dark:text-primary-300'
                        : 'border-surface-overlay text-text-secondary hover:bg-surface-overlay/40'
                    }`}
                  >
                    {REMINDER_CHANNEL_LABEL[c]}
                  </button>
                )
              })}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1.5">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional notes"
              rows={2}
              className="input input-bordered w-full"
              disabled={isSubmitting}
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn btn-ghost" disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <span className="loading loading-spinner loading-sm" /> Adding…
                </>
              ) : (
                'Add reminder'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
